import React, { useState, useEffect } from 'react';
import { FormGroup, Label, Input, Card, CardBody, FormFeedback, Button, Collapse, Col, Row, CardHeader } from 'reactstrap';
import AppTokenConfiguration from './AppTokenConfiguration';
import TriggerConfiguration from './TriggerConfiguration';
import { useTranslation } from 'react-i18next'
import { CoreModuleView } from '../../../models/CoreModels';


interface BaseConfigurationProps {
    originalModule: CoreModuleView
    disabled: boolean
    updatedModule: CoreModuleView
    setUpdateModule: (arg: any) => void
    moduleId?: string
    updateValidation: (arg: boolean) => void
    additionalAppTokenScopes: string[]
}

const validate = (updatedModule: CoreModuleView) => {
    const validation: string[] = [];
    if (!updatedModule.title) {
        validation.push('title');
    }
    return validation;
}

const BaseConfiguration = (props: BaseConfigurationProps) => {
    const { t } = useTranslation(['configuration', 'common']);
    const [open, setOpen] = useState<boolean>(true);
    const [appTokenInvalid, setAppTokenInvalid] = useState<boolean>(false);

    const validation = validate(props.updatedModule);

    useEffect(() => {
        validateBaseConfiguration(props.updatedModule)
    }, []);

    useEffect(() => {
        validateBaseConfiguration(props.updatedModule)
    }, [appTokenInvalid])

    const validateBaseConfiguration = (module: CoreModuleView) => {
        const invalidBaseConfiguration = validate(module).length ? true : false
        const invalid = invalidBaseConfiguration || appTokenInvalid;
        props.updateValidation(invalid);
    }

    const updateConfiguration = (p: Partial<CoreModuleView>) => {
        let mergedModule = { ...props.updatedModule, ...p }
        props.setUpdateModule(mergedModule);
        validateBaseConfiguration(mergedModule)
    }

    return (
        <Card className="base_module edit_module">
            <CardHeader tag="h2">
                <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen(!open)}>{open ? '-' : '+'}</Button>
                {t('moduleView.baseModule.cardTitle')}
            </CardHeader>
            <Collapse isOpen={open}>
                <Row>
                    <Col sm={props.originalModule.appToken ? "6" : "12"}>
                        <CardBody>
                            <FormGroup check>
                                <div style={({ display: 'grid', gridTemplateColumns: '1fr 1fr' })}>
                                    <Label check>
                                        <Input
                                            type="checkbox"
                                            name="active"
                                            id="active"
                                            checked={props.updatedModule.active}
                                            disabled={props.disabled}
                                            onChange={(e) => updateConfiguration({ active: e.target.checked })} />
                                        {t('status.active', { ns: 'common' })}
                                    </Label>
                                    {(props.originalModule.dryRunEnabled) &&
                                        <Label check>
                                            <Input
                                                type="checkbox"
                                                name="dryRun"
                                                id="dryRun"
                                                checked={props.updatedModule.dryRun}
                                                disabled={props.disabled}
                                                onChange={(e) => updateConfiguration({ dryRun: e.target.checked })} />
                                            {t('moduleView.baseModule.dryRun')}
                                        </Label>}
                                </div>
                            </FormGroup>
                            <FormGroup>
                                <Label for="title">{t('moduleView.baseModule.title')}</Label>
                                <Input type="text" name="title" id="title"
                                    placeholder={t('moduleView.baseModule.title')}
                                    value={props.updatedModule.title || ''}
                                    disabled={props.disabled}
                                    onChange={(e) => updateConfiguration({ title: e.target.value })}
                                    invalid={validation.indexOf('title') !== -1}
                                    autoComplete="off" />
                                <FormFeedback>{t('moduleView.baseModule.titleRequired')}</FormFeedback>
                            </FormGroup>
                            {props.updatedModule.sourceEnvironment &&
                                <FormGroup>
                                    <Label for="sourceEnvironment">{t('moduleView.baseModule.sourceEnvironment')}</Label>
                                    <Input
                                        type="text"
                                        name="sourceEnvironment"
                                        id="sourceEnvironment"
                                        value={props.updatedModule.sourceEnvironment}
                                        disabled={true}
                                        autoComplete="off" />
                                </FormGroup>
                            }
                            {props.originalModule.runnable &&
                                <TriggerConfiguration
                                    moduleTriggers={props.updatedModule.moduleTriggers || []}
                                    disableInput={props.disabled}
                                    onUpdateTriggers={triggers => updateConfiguration({ moduleTriggers: triggers })}
                                    moduleId={props.moduleId}
                                />
                            }

                        </CardBody>
                    </Col>
                    {props.updatedModule.appToken &&
                        <Col sm="6" className="apptoken-configuration">
                            <AppTokenConfiguration
                                appToken={props.updatedModule.appToken}
                                active={props.updatedModule.active}
                                additionalScopes={props.additionalAppTokenScopes}
                                invalid={(value) => setAppTokenInvalid(value)}
                                disabled={props.disabled}
                            />
                        </Col>
                    }
                </Row>
            </Collapse>
        </Card>
    )
}

export default BaseConfiguration;
